import { useEffect, useRef, useState } from 'react';

const TARGETING_COOKIE_ARRAY = ['C0002', 'C0004'];

const stringToArray = (str: string) => (str ? str.slice(1, -1).split(',') : []);

const isAcceptedAllCookies = (cookieList: string[]) =>
    cookieList.some((value) => TARGETING_COOKIE_ARRAY.includes(value));

type UseOneTrustInitParams = {
    onConsent?: () => void;
};

type UseOneTrustResult = {
    isBannerClosed: boolean;
};

export const useOneTrust = ({ onConsent }: UseOneTrustInitParams): UseOneTrustResult => {
    const consentCalledRef = useRef(false);
    const onConsentChangedRegistered = useRef(false);
    const [isBannerClosed, setIsBannerClosed] = useState(() => Boolean(window.OneTrust?.IsAlertBoxClosed?.()));

    useEffect(() => {
        let observer: MutationObserver | null = null;

        const interval = setInterval(() => {
            const oneTrust = window.OneTrust;

            if (!oneTrust) return;

            const closed = Boolean(oneTrust.IsAlertBoxClosed?.());
            setIsBannerClosed(closed);

            if (closed) {
                const cookieList = stringToArray(window.OptanonActiveGroups);
                const isAllowed = isAcceptedAllCookies(cookieList);

                if (isAllowed && !consentCalledRef.current) {
                    consentCalledRef.current = true;
                    onConsent?.();
                }

                clearInterval(interval);
                
return;
            }

            if (oneTrust.OnConsentChanged && !onConsentChangedRegistered.current) {
                onConsentChangedRegistered.current = true;

                oneTrust.OnConsentChanged(({ detail }: { detail: string[] }) => {
                    setIsBannerClosed(true);

                    const isAllowed = isAcceptedAllCookies(detail);

                    if (isAllowed && !consentCalledRef.current) {
                        consentCalledRef.current = true;
                        onConsent?.();
                    }
                });
            }

            // Watch for banner hidden via CSS class (e.g. X/dismiss button).
            // Only set up when the real DOM element exists — skipped in test environments.
            const bannerEl = document.getElementById('onetrust-banner-sdk');
            if (bannerEl) {
                observer = new MutationObserver(() => {
                    const el = document.getElementById('onetrust-banner-sdk');
                    if (!el || getComputedStyle(el).display === 'none') {
                        observer?.disconnect();
                        observer = null;
                        setIsBannerClosed(true);
                    }
                });
                observer.observe(bannerEl, { attributes: true, attributeFilter: ['style', 'class'] });
                if (bannerEl.parentElement) {
                    observer.observe(bannerEl.parentElement, { childList: true });
                }
            }

            clearInterval(interval);
        }, 200);

        return () => {
            clearInterval(interval);
            observer?.disconnect();
        };
    }, [onConsent]);

    return { isBannerClosed };
};
